import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { merchantService, type MerchantLocation, type MerchantPlan } from '@/services/merchant.service';
import type { MerchantFilters, MerchantFormData, MerchantBankingInfo } from '@/types/merchant';

// Query keys
export const merchantKeys = {
  all: ['merchants'] as const,
  lists: () => [...merchantKeys.all, 'list'] as const,
  list: (filters: MerchantFilters) => [...merchantKeys.lists(), filters] as const,
  details: () => [...merchantKeys.all, 'detail'] as const,
  detail: (id: string) => [...merchantKeys.details(), id] as const,
  locations: (merchantId: string) => [...merchantKeys.all, merchantId, 'locations'] as const,
  plans: (merchantId: string) => [...merchantKeys.all, merchantId, 'plans'] as const,
};

// Get merchants list
export function useMerchants(filters?: MerchantFilters) {
  return useQuery({
    queryKey: merchantKeys.list(filters || {}),
    queryFn: () => merchantService.getMerchants(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Get single merchant
export function useMerchant(id: string) {
  return useQuery({
    queryKey: merchantKeys.detail(id),
    queryFn: () => merchantService.getMerchantById(id),
    enabled: !!id,
  });
}

// Create merchant
export function useCreateMerchant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MerchantFormData) => merchantService.createMerchant(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: merchantKeys.lists() });
    },
  });
}

// Update merchant
export function useUpdateMerchant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<MerchantFormData> }) =>
      merchantService.updateMerchant(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: merchantKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: merchantKeys.lists() });
    },
  });
}

// Delete merchant
export function useDeleteMerchant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => merchantService.deleteMerchant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: merchantKeys.lists() });
    },
  });
}

// Approve merchant
export function useApproveMerchant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ merchantId, notes }: { merchantId: string; notes?: string }) =>
      merchantService.approveMerchant(merchantId, notes),
    onSuccess: (_, { merchantId }) => {
      queryClient.invalidateQueries({ queryKey: merchantKeys.detail(merchantId) });
      queryClient.invalidateQueries({ queryKey: merchantKeys.lists() });
    },
  });
}

// Get merchant locations
export function useMerchantLocations(merchantId: string) {
  return useQuery<MerchantLocation[]>({
    queryKey: merchantKeys.locations(merchantId),
    queryFn: () => merchantService.getLocations(merchantId),
    enabled: !!merchantId,
  });
}

// Get merchant plans
export function useMerchantPlans(merchantId: string) {
  return useQuery<MerchantPlan[]>({
    queryKey: merchantKeys.plans(merchantId),
    queryFn: () => merchantService.getPlans(merchantId),
    enabled: !!merchantId,
  });
}

// Banking-specific hooks
export function useBankingInfo(merchantId: string) {
  return useQuery({
    queryKey: [...merchantKeys.all, merchantId, 'banking'],
    queryFn: () => merchantService.getBankingInfo(merchantId),
    enabled: !!merchantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useUpdateBankingInfo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      merchantId,
      bankingData,
      accountNumber,
    }: {
      merchantId: string;
      bankingData: MerchantBankingInfo;
      accountNumber?: string;
    }) =>
      merchantService.updateBankingInfo(merchantId, bankingData, accountNumber),
    onSuccess: (_, { merchantId }) => {
      queryClient.invalidateQueries({ queryKey: merchantKeys.detail(merchantId) });
      queryClient.invalidateQueries({ queryKey: [...merchantKeys.all, merchantId, 'banking'] });
    },
  });
}

export function useVerifyBankAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      merchantId, 
      depositAmounts 
    }: { 
      merchantId: string; 
      depositAmounts: { amount1: number; amount2: number } 
    }) => merchantService.verifyBankAccount(merchantId, depositAmounts),
    onSuccess: (_, { merchantId }) => {
      queryClient.invalidateQueries({ queryKey: merchantKeys.detail(merchantId) });
      queryClient.invalidateQueries({ queryKey: [...merchantKeys.all, merchantId, 'banking'] });
    },
  });
}
